import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Menu, MenuItem,
  Snackbar, TextField, Typography,
} from '@mui/material';
import {
  ArrowUpDown,
  MoreVertical,
  Plus as AddIcon,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cloneClaim, createDraft, deleteClaim, listClaims } from '../../api/claimsClient';
import {
  fetchOptumDocument,
  listOptumClaimDocuments,
  listOptumOnboardings,
  prepareOptumClaimDocuments,
  triggerOptumSync,
} from '../../api/optumApi';
import { PdfViewerModal } from '../../components/documents/PdfViewerModal';

const DASH = '\u2014';

const STATUS_DEFS = {
  draft: { label: 'Draft', fg: '#64748B', bg: '#F1F3F9', border: 'transparent' },
  validated: { label: 'Validated', fg: '#FFFFFF', bg: '#1D4ED8', border: 'transparent' },
  submitted: { label: 'Submitted', fg: '#FFFFFF', bg: '#000000', border: 'transparent' },
  paid: { label: 'Paid', fg: '#1F8A4C', bg: '#E6F4EC', border: 'transparent' },
  partially_paid: { label: 'Partial', fg: '#B7791F', bg: '#FFFBEB', border: '#FBBF24' },
  denied: { label: 'Denied', fg: '#FF0004', bg: '#FD8A8A', border: 'transparent' },
  rejected: { label: 'Rejected', fg: '#FF0004', bg: '#FFFFFF', border: '#FF0004' },
};

const STATUS_ORDER = ['draft', 'validated', 'submitted', 'paid', 'partially_paid', 'denied', 'rejected'];

const STATUS_ALIASES = {
  draft: 'draft',
  validated: 'validated',
  ready_to_submit: 'validated',
  submitted: 'submitted',
  accepted: 'submitted',
  pending_payer: 'submitted',
  paid: 'paid',
  partially_paid: 'partially_paid',
  partial: 'partially_paid',
  denied: 'denied',
  rejected: 'rejected',
};

const ACTION_BY_STATUS = {
  draft: { label: 'Continue', variant: 'filled', key: 'open' },
  // 'validated' claims may have no submission at all; the tracking projection
  // returns a not_submitted state and TrackPage renders that honestly.
  validated: { label: 'Track', variant: 'outline', key: 'track' },
  submitted: { label: 'Track', variant: 'outline', key: 'track' },
  paid: { label: 'View EOB', variant: 'outline', key: 'view_eob' },
  partially_paid: { label: 'View EOB', variant: 'outline', key: 'view_eob' },
  denied: { label: 'Appeal', variant: 'filled', key: 'appeal' },
  rejected: { label: 'Rebuild', variant: 'filled', key: 'rebuild' },
};

// The map above is keyed on STATUS alone, but `view_eob` is not a status-level
// action: its handler is entirely Optum-specific (listOptumClaimDocuments,
// prepareOptumClaimDocuments, fetchOptumDocument), so on any other source it
// queries Optum about a claim Optum has never seen and reports that no EOB is
// available. That never surfaced while Optum was the only source that could
// reach `paid`; a Claim.MD remittance now publishes its decision too.
//
// Those claims are not without a document — the remittance PDF is fetched and
// stored against the ERA artifact — but it is served from the Track page, which
// owns the ERA capabilities and the withheld-reason copy explaining a PDF that
// is deliberately not offered (a multi-claim remittance names other patients'
// claims). Sending the operator there is the honest action, not a second
// download path that would have to restate those rules.
function primaryAction(claim, status) {
  const action = ACTION_BY_STATUS[status] || ACTION_BY_STATUS.draft;
  if (action.key === 'view_eob' && claim.source !== 'optum') {
    return ACTION_BY_STATUS.submitted;
  }
  return action;
}

const pageStyles = {
  page: {
    minHeight: '100%',
    background: '#F8FAFC',
    padding: '28px',
    color: '#111827',
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  shell: {
    maxWidth: 1320,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 22,
  },
  title: {
    margin: 0,
    fontSize: 28,
    lineHeight: '34px',
    fontWeight: 750,
    letterSpacing: 0,
  },
  subtitle: {
    margin: '6px 0 0',
    color: '#64748B',
    fontSize: 14,
  },
  headerActions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 10,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: 12,
    marginBottom: 16,
  },
  filterPanel: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: 14,
    marginBottom: 14,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  controls: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1fr) minmax(170px, 220px) auto',
    gap: 10,
    alignItems: 'center',
  },
  searchWrap: {
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748B',
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    height: 40,
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    padding: '0 12px 0 38px',
    fontSize: 14,
    outline: 'none',
    background: '#FFFFFF',
  },
  select: {
    height: 40,
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    padding: '0 12px',
    fontSize: 14,
    color: '#0F172A',
    background: '#FFFFFF',
  },
  resultCount: {
    textAlign: 'right',
    color: '#64748B',
    fontSize: 13,
    whiteSpace: 'nowrap',
  },
  tableWrap: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    minWidth: 1040,
    borderCollapse: 'collapse',
  },
  th: {
    padding: '12px 14px',
    borderBottom: '1px solid #E2E8F0',
    color: '#475569',
    fontSize: 11,
    fontWeight: 750,
    textTransform: 'uppercase',
    letterSpacing: 0,
    textAlign: 'left',
    background: '#F8FAFC',
  },
  td: {
    padding: '14px',
    borderBottom: '1px solid #F1F5F9',
    fontSize: 14,
    verticalAlign: 'middle',
  },
  row: {
    cursor: 'pointer',
  },
  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    fontSize: 13,
  },
  patient: {
    fontWeight: 700,
    color: '#111827',
  },
  muted: {
    color: '#64748B',
  },
  actionCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 6,
  },
};

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoStr(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function normalizeStatus(claim) {
  const raw = claim.effective_status || claim.draft_status || '';
  const key = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
  return STATUS_ALIASES[key] || key || 'draft';
}

function money(value) {
  if (value === null || value === undefined || value === '') return DASH;
  const num = Number(value);
  if (Number.isNaN(num)) return DASH;
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDate(value) {
  if (!value) return DASH;
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleDateString();
}

function display(value) {
  return value || DASH;
}

function isEobDocument(document) {
  const canonicalType = String(document?.canonical_document_type || '').toLowerCase();
  const rawType = String(document?.document_type || '').toLowerCase();
  const fileName = String(document?.file_name || '').toLowerCase();
  return canonicalType === 'eob' || rawType === 'eob' || fileName.includes('eob');
}

function canQueueOptumDocument(document) {
  return ['pending', 'error'].includes(String(document?.fetch_status || 'pending').toLowerCase());
}

async function getPreparedEobDocuments(claimId, initialDocuments) {
  const firstPass = initialDocuments.filter(isEobDocument);
  if (firstPass.length > 0) {
    return { eobDocuments: firstPass, prepared: null };
  }
  const prepared = await prepareOptumClaimDocuments(claimId);
  const preparedDocuments = Array.isArray(prepared?.documents) ? prepared.documents : [];
  return {
    eobDocuments: preparedDocuments.filter(isEobDocument),
    prepared,
  };
}

// The Claim # column, in order of how strongly each number identifies THIS
// claim. `patient_account_number` is deliberately absent: it is CMS-1500 Box 26,
// mirrored from the patient, so every claim for one patient carries it and four
// rows for the same patient all read alike. The claim-scoped numbers are the
// payer's own (835 CLP07, once a remittance teaches it) and ours (837P CLM01,
// frozen at submission) — see claims/claim_numbers.py. A claim that was never
// submitted has neither, and falls back to its own id.
function claimReferenceNumber(claim) {
  return (
    claim.payer_claim_number
    || claim.submitter_control_number
    || String(claim.id).slice(0, 8).toUpperCase()
  );
}

function compareClaims(a, b, sort) {
  const direction = sort.direction === 'asc' ? 1 : -1;
  const aValue = getSortValue(a, sort.key);
  const bValue = getSortValue(b, sort.key);
  if (aValue < bValue) return -1 * direction;
  if (aValue > bValue) return 1 * direction;
  return 0;
}

function getSortValue(claim, key) {
  if (key === 'amount') return Number(claim.total_charge || 0);
  if (key === 'status') return normalizeStatus(claim);
  if (key === 'service') return claim.earliest_service_date || '';
  if (key === 'claim_number') return claimReferenceNumber(claim).toLowerCase();
  return String(claim[key] || '').toLowerCase();
}

const ClaimListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatusFilter = (() => {
    const raw = (searchParams.get('effective_status') || '').toLowerCase();
    return STATUS_ALIASES[raw] || (raw && STATUS_DEFS[raw] ? raw : 'all');
  })();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [payerFilter, setPayerFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'created_at', direction: 'desc' });
  const [actionBusy, setActionBusy] = useState(false);
  const [viewer, setViewer] = useState({
    open: false,
    documentType: null,
    documentId: null,
    documentLabel: '',
  });

  const [hasOptum, setHasOptum] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncStart, setSyncStart] = useState(daysAgoStr(30));
  const [syncEnd, setSyncEnd] = useState(todayStr());
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [moreMenu, setMoreMenu] = useState({ anchorEl: null, claim: null });

  // Always fetch the full claim list — the stats cards and chip counts need
  // global totals regardless of the active status filter, which server-side
  // filtering would otherwise clip to the filtered subset. Status filtering
  // happens entirely client-side (see `filtered` below).
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listClaims();
      setClaims(rows);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load claims.');
    } finally {
      setLoading(false);
    }

    try {
      const onboardings = await listOptumOnboardings();
      setHasOptum(onboardings.length > 0);
    } catch {
      setHasOptum(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep ?effective_status= in the URL synced with the active filter, so deep links
  // and page refreshes preserve state.
  useEffect(() => {
    const current = searchParams.get('effective_status');
    if (statusFilter === 'all') {
      if (current) {
        const next = new URLSearchParams(searchParams);
        next.delete('effective_status');
        setSearchParams(next, { replace: true });
      }
    } else if (current !== statusFilter) {
      const next = new URLSearchParams(searchParams);
      next.set('effective_status', statusFilter);
      setSearchParams(next, { replace: true });
    }
  }, [statusFilter, searchParams, setSearchParams]);

  const counts = useMemo(() => {
    const next = Object.fromEntries(STATUS_ORDER.map((status) => [status, 0]));
    claims.forEach((claim) => {
      const status = normalizeStatus(claim);
      if (next[status] !== undefined) next[status] += 1;
    });
    return next;
  }, [claims]);

  const payerOptions = useMemo(() => {
    const names = claims.map((claim) => claim.payer_name).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [claims]);

  const stats = useMemo(() => ([
    { label: 'Drafts open', value: counts.draft, cta: 'Complete ready claims', tone: 'muted' },
    { label: 'Awaiting payer', value: counts.submitted + counts.validated, cta: 'Track payer response', tone: 'warn' },
    { label: 'Rejected', value: counts.rejected, cta: 'Rebuild and resubmit', tone: 'danger', highlight: true },
    {
      label: 'Denied (recoverable)',
      value: money(claims.filter((claim) => normalizeStatus(claim) === 'denied').reduce(
        (total, claim) => total + Number(claim.total_charge || 0),
        0,
      )),
      cta: 'Start appeal work',
      tone: 'danger',
      highlight: true,
    },
  ]), [claims, counts]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return claims
      .filter((claim) => statusFilter === 'all' || normalizeStatus(claim) === statusFilter)
      .filter((claim) => payerFilter === 'all' || claim.payer_name === payerFilter)
      .filter((claim) => {
        if (!needle) return true;
        return [
          claim.patient_display_name,
          claim.payer_claim_number,
          claim.submitter_control_number,
          claim.patient_account_number,
          claim.payer_name,
        ].some((value) => String(value || '').toLowerCase().includes(needle));
      })
      .slice()
      .sort((a, b) => compareClaims(a, b, sort));
  }, [claims, payerFilter, search, sort, statusFilter]);

  // Some statuses are filtered server-side (see BACKEND_EFFECTIVE_STATUS_VALUES), so
  // `claims` itself can already be empty when a filter matches nothing. Base the
  // empty-state choice on whether a filter is active rather than on claims.length.
  const hasActiveFilters = statusFilter !== 'all' || payerFilter !== 'all' || Boolean(search.trim());

  const handleCreate = async () => {
    const fresh = await createDraft();
    navigate(`/claims/${fresh.id}`);
  };

  const handleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleAction = async (event, actionKey, claim) => {
    event.stopPropagation();
    if (actionKey === 'appeal') {
      navigate(`/claims/${claim.id}/appeal`);
      return;
    }
    if (actionKey === 'track') {
      navigate(`/claims/${claim.id}/track`);
      return;
    }
    if (actionKey === 'view_ticket') {
      navigate(`/claims/${claim.id}/ticket/${claim.action_ticket_id}`);
      return;
    }
    if (actionKey === 'rebuild') {
      setActionBusy(true);
      try {
        const fresh = await cloneClaim(claim.id);
        setSnackbar('Claim copy ready');
        navigate(`/claims/${fresh.id}`);
      } catch (err) {
        setSnackbar(err.response?.data?.detail || 'Could not clone claim - try again');
      } finally {
        setActionBusy(false);
      }
      return;
    }
    if (actionKey === 'view_eob') {
      setActionBusy(true);
      try {
        const documents = await listOptumClaimDocuments(claim.id);
        const { eobDocuments, prepared } = await getPreparedEobDocuments(claim.id, documents);
        // Quick task 260518-u54: the raw download_url is no longer serialized.
        // A "downloaded" EOB is one with an UploadedDocument id we can request
        // a signed view URL for; everything else still needs the Optum fetch.
        const downloadedEob = eobDocuments.find((document) => document.uploaded_document_id);
        const fetchableEob = eobDocuments.find((document) => document.id && canQueueOptumDocument(document));
        if (downloadedEob?.uploaded_document_id) {
          // Quick task 260518-wnv: open the in-app PdfViewerModal instead of
          // window.open. The modal owns the requestSignedView lifecycle
          // (including silent re-issue ~60s before the signed URL expires).
          setViewer({
            open: true,
            documentType: 'eob',
            documentId: downloadedEob.uploaded_document_id,
            documentLabel: 'EOB',
          });
        } else if (fetchableEob?.id) {
          await fetchOptumDocument(fetchableEob.id);
          setSnackbar('EOB download queued - try View EOB again shortly');
        } else if ((prepared?.detail_fetches_queued || 0) > 0) {
          setSnackbar('Optum EOB details queued - try View EOB again shortly');
        } else {
          setSnackbar('No downloaded EOB found for this claim yet');
        }
      } catch (err) {
        setSnackbar(err.response?.data?.detail || 'Could not open EOB - try again');
      } finally {
        setActionBusy(false);
      }
      return;
    }
    navigate(`/claims/${claim.id}`);
  };

  const handleMore = (event, claim) => {
    event.stopPropagation();
    setMoreMenu({ anchorEl: event.currentTarget, claim });
  };

  const closeMoreMenu = () => {
    setMoreMenu({ anchorEl: null, claim: null });
  };

  const handleMoreAppeal = () => {
    const claim = moreMenu.claim;
    closeMoreMenu();
    if (claim) navigate(`/claims/${claim.id}/appeal`);
  };

  const handleDelete = async (event, claim) => {
    event.stopPropagation();
    const claimLabel = claim.patient_display_name || claim.patient_account_number || claim.id;
    if (!window.confirm(`Delete claim for ${claimLabel}? This cannot be undone.`)) return;
    setActionBusy(true);
    try {
      await deleteClaim(claim.id);
      setClaims((current) => current.filter((row) => row.id !== claim.id));
      setSnackbar('Claim deleted');
    } catch (err) {
      setSnackbar(err.response?.data?.detail || 'Could not delete claim - try again');
    } finally {
      setActionBusy(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setPayerFilter('all');
  };

  const handleSyncOpen = () => {
    setSyncStart(daysAgoStr(30));
    setSyncEnd(todayStr());
    setSyncError(null);
    setSyncOpen(true);
  };

  const handleSyncClose = () => {
    if (syncing) return;
    setSyncOpen(false);
    setSyncError(null);
  };

  const handleSyncConfirm = async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      await triggerOptumSync(syncStart, syncEnd);
      setSyncOpen(false);
      setSnackbar('Sync started - claims will appear shortly');
    } catch (err) {
      setSyncError(err.response?.data?.detail || 'Sync failed. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box style={pageStyles.page}>
      <Box style={pageStyles.shell}>
        <PageHeader
          hasOptum={hasOptum}
          onCreate={handleCreate}
          onRefresh={load}
          onSync={handleSyncOpen}
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && claims.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            <StatsGrid stats={stats} />
            <FilterRow
              counts={counts}
              filteredCount={filtered.length}
              totalCount={claims.length}
              payerFilter={payerFilter}
              payerOptions={payerOptions}
              search={search}
              statusFilter={statusFilter}
              onPayerChange={setPayerFilter}
              onSearchChange={setSearch}
              onStatusChange={setStatusFilter}
            />

            <ClaimTable
              claims={filtered}
              sort={sort}
              onMore={handleMore}
              onOpenClaim={(claim) => navigate(`/claims/${claim.id}`)}
              onDelete={handleDelete}
              onPrimaryAction={handleAction}
              onSort={handleSort}
              actionBusy={actionBusy}
            />
            <RowMoreMenu
              anchorEl={moreMenu.anchorEl}
              claim={moreMenu.claim}
              onAppeal={handleMoreAppeal}
              onClose={closeMoreMenu}
            />

            {filtered.length === 0 && !hasActiveFilters && <EmptyState type="empty" onCreate={handleCreate} />}
            {filtered.length === 0 && hasActiveFilters && <EmptyState type="filtered" onClear={clearFilters} />}
          </>
        )}

        <OptumSyncDialog
          open={syncOpen}
          syncing={syncing}
          syncError={syncError}
          syncStart={syncStart}
          syncEnd={syncEnd}
          onClose={handleSyncClose}
          onConfirm={handleSyncConfirm}
          onStartChange={setSyncStart}
          onEndChange={setSyncEnd}
        />
        <Snackbar
          open={Boolean(snackbar)}
          autoHideDuration={4000}
          onClose={() => setSnackbar(null)}
          message={snackbar}
        />
        <PdfViewerModal
          open={viewer.open}
          onClose={() => setViewer((s) => ({ ...s, open: false }))}
          documentType={viewer.documentType}
          documentId={viewer.documentId}
          documentLabel={viewer.documentLabel}
        />
      </Box>
    </Box>
  );
};

function PageHeader({ hasOptum, onCreate, onRefresh, onSync }) {
  return (
    <div style={pageStyles.header}>
      <div>
        <h1 style={pageStyles.title}>Claims</h1>
        <p style={pageStyles.subtitle}>Monitor claim readiness, payer outcomes, and appeal opportunities.</p>
      </div>
      <div style={pageStyles.headerActions}>
        <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={onRefresh}>Refresh</Button>
        {hasOptum && (
          <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={onSync}>
            Sync from Optum
          </Button>
        )}
        <Button variant="contained" startIcon={<AddIcon size={16} />} onClick={onCreate}>Create Claim</Button>
      </div>
    </div>
  );
}

function StatsGrid({ stats }) {
  return (
    <div style={pageStyles.statsGrid}>
      {stats.map((stat) => <AttentionStat key={stat.label} {...stat} />)}
    </div>
  );
}

function AttentionStat({ label, value, cta, tone, highlight }) {
  const palette = {
    muted: { accent: '#64748B', bg: '#FFFFFF' },
    warn: { accent: '#B7791F', bg: '#FFFFFF' },
    danger: { accent: '#FF0004', bg: '#FFFFFF' },
  }[tone];
  return (
    <div
      style={{
        background: palette.bg,
        border: highlight ? `1px solid ${palette.accent}` : '1px solid #E2E8F0',
        borderLeft: `4px solid ${palette.accent}`,
        borderRadius: 8,
        padding: '14px 16px',
        minHeight: 112,
      }}
    >
      <div style={{ color: '#64748B', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ color: '#0F172A', fontSize: 30, lineHeight: '38px', fontWeight: 800, marginTop: 8 }}>
        {value}
      </div>
      <div style={{ color: palette.accent, fontSize: 13, fontWeight: 700, marginTop: 4 }}>
        {cta}
      </div>
    </div>
  );
}

function FilterRow({
  counts,
  filteredCount,
  totalCount,
  payerFilter,
  payerOptions,
  search,
  statusFilter,
  onPayerChange,
  onSearchChange,
  onStatusChange,
}) {
  return (
    <div style={pageStyles.filterPanel}>
      <div style={pageStyles.chipRow}>
        <FilterChip
          active={statusFilter === 'all'}
          label="All"
          count={totalCount}
          onClick={() => onStatusChange('all')}
        />
        {STATUS_ORDER.map((status) => (
          <FilterChip
            key={status}
            active={statusFilter === status}
            label={STATUS_DEFS[status].label}
            count={counts[status]}
            onClick={() => onStatusChange(status)}
          />
        ))}
      </div>
      <div style={pageStyles.controls}>
        <div style={pageStyles.searchWrap}>
          <Search size={16} style={pageStyles.searchIcon} />
          <input
            aria-label="Search claims"
            placeholder="Search claims"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            style={pageStyles.searchInput}
          />
        </div>
        <select
          aria-label="Payer"
          value={payerFilter}
          onChange={(event) => onPayerChange(event.target.value)}
          style={pageStyles.select}
        >
          <option value="all">All payers</option>
          {payerOptions.map((payer) => <option key={payer} value={payer}>{payer}</option>)}
        </select>
        <div style={pageStyles.resultCount}>{filteredCount} of {totalCount} claims</div>
      </div>
    </div>
  );
}

function FilterChip({ active, count, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: active ? '1px solid #000000' : '1px solid #CBD5E1',
        background: active ? '#000000' : '#FFFFFF',
        color: active ? '#FFFFFF' : '#0F172A',
        borderRadius: 999,
        padding: '7px 12px',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {label} {count}
    </button>
  );
}

function ClaimTable({ claims, sort, actionBusy, onMore, onOpenClaim, onDelete, onPrimaryAction, onSort }) {
  if (claims.length === 0) return null;
  const columns = [
    ['claim_number', 'Claim #'],
    ['patient_display_name', 'Patient'],
    ['payer_name', 'Payer'],
    ['billing_provider_name', 'Provider'],
    ['service', 'Service'],
    ['amount', 'Amount'],
    ['status', 'Status'],
    ['denial_code', 'Code'],
  ];
  return (
    <div style={pageStyles.tableWrap}>
      <table style={pageStyles.table}>
        <thead>
          <tr>
            {columns.map(([key, label]) => <Th key={key} sortKey={key} label={label} sort={sort} onSort={onSort} />)}
            <th style={{ ...pageStyles.th, textAlign: 'right' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {claims.map((claim) => (
            <ClaimRow
              key={claim.id}
              claim={claim}
              actionBusy={actionBusy}
              onDelete={onDelete}
              onMore={onMore}
              onOpenClaim={onOpenClaim}
              onPrimaryAction={onPrimaryAction}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ label, sortKey, sort, onSort }) {
  return (
    <th style={pageStyles.th}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          border: 0,
          padding: 0,
          background: 'transparent',
          color: 'inherit',
          font: 'inherit',
          textTransform: 'inherit',
          cursor: 'pointer',
        }}
      >
        {label}
        <ArrowUpDown size={13} strokeWidth={sort.key === sortKey ? 2.5 : 1.75} />
      </button>
    </th>
  );
}

function ClaimRow({ claim, actionBusy, onDelete, onMore, onOpenClaim, onPrimaryAction }) {
  const status = normalizeStatus(claim);
  const action = primaryAction(claim, status);
  return (
    <tr style={pageStyles.row} onClick={() => onOpenClaim(claim)}>
      <td style={{ ...pageStyles.td, ...pageStyles.mono }}>{display(claimReferenceNumber(claim))}</td>
      <td style={pageStyles.td}>
        <div style={pageStyles.patient}>{display(claim.patient_display_name)}</div>
        <div style={{ ...pageStyles.muted, fontSize: 12 }}>{String(claim.id).slice(0, 8)}</div>
      </td>
      <td style={pageStyles.td}>{display(claim.payer_name)}</td>
      <td style={pageStyles.td}>{display(claim.billing_provider_name)}</td>
      <td style={pageStyles.td}>{formatDate(claim.earliest_service_date)}</td>
      <td style={pageStyles.td}>{money(claim.total_charge)}</td>
      <td style={pageStyles.td}><StatusPill status={status} raw={claim.effective_status || claim.draft_status} /></td>
      <td style={{ ...pageStyles.td, ...pageStyles.mono }}>{display(claim.denial_code)}</td>
      <td style={{ ...pageStyles.td, textAlign: 'right' }}>
        <RowActions
          action={action}
          busy={actionBusy}
          claim={claim}
          onMore={onMore}
          onDelete={onDelete}
          onPrimaryAction={onPrimaryAction}
        />
      </td>
    </tr>
  );
}

function StatusPill({ status, raw }) {
  const def = STATUS_DEFS[status] || {
    label: String(raw || status || 'Unknown').replace(/_/g, ' '),
    fg: '#64748B',
    bg: '#F1F3F9',
    border: 'transparent',
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 26,
        borderRadius: 999,
        padding: '0 10px',
        background: def.bg,
        color: def.fg,
        border: `1px solid ${def.border}`,
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {def.label}
    </span>
  );
}

function isOptumClaim(claim) {
  return String(claim?.source || '').toLowerCase() === 'optum';
}

function RowActions({ action, busy, claim, onDelete, onMore, onPrimaryAction }) {
  return (
    <div style={pageStyles.actionCell}>
      {claim.action_ticket_id && (
        <button
          type="button"
          onClick={(event) => onPrimaryAction(event, 'view_ticket', claim)}
          style={{
            border: '1px solid #CBD5E1',
            background: '#FFFFFF',
            color: '#0F172A',
            borderRadius: 6,
            height: 34,
            padding: '0 12px',
            fontSize: 13,
            fontWeight: 800,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          View ticket
        </button>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={(event) => onPrimaryAction(event, action.key, claim)}
        style={{
          border: action.variant === 'filled' ? '1px solid #000000' : '1px solid #CBD5E1',
          background: action.variant === 'filled' ? '#000000' : '#FFFFFF',
          color: action.variant === 'filled' ? '#FFFFFF' : '#0F172A',
          borderRadius: 6,
          minWidth: 86,
          height: 34,
          padding: '0 12px',
          fontSize: 13,
          fontWeight: 800,
          cursor: busy ? 'wait' : 'pointer',
        }}
      >
        {action.label}
      </button>
      <button
        type="button"
        aria-label={`More actions for ${claim.patient_display_name || claim.patient_account_number || claim.id}`}
        onClick={(event) => onMore(event, claim)}
        style={{
          width: 34,
          height: 34,
          borderRadius: 6,
          border: '1px solid #CBD5E1',
          background: '#FFFFFF',
          color: '#475569',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <MoreVertical size={16} />
      </button>
      <button
        type="button"
        aria-label={`Delete claim ${claim.patient_display_name || claim.patient_account_number || claim.id}`}
        disabled={busy}
        onClick={(event) => onDelete(event, claim)}
        style={{
          width: 34,
          height: 34,
          borderRadius: 6,
          border: '1px solid #FECACA',
          background: '#FFFFFF',
          color: '#B91C1C',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: busy ? 'wait' : 'pointer',
        }}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

function RowMoreMenu({ anchorEl, claim, onAppeal, onClose }) {
  const open = Boolean(anchorEl);
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      onClick={(event) => event.stopPropagation()}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {isOptumClaim(claim) ? (
        <MenuItem onClick={onAppeal}>Appeal with Optum</MenuItem>
      ) : (
        <MenuItem disabled>No additional actions</MenuItem>
      )}
    </Menu>
  );
}

function EmptyState({ type, onClear, onCreate }) {
  const isEmpty = type === 'empty';
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 8,
        padding: 40,
        textAlign: 'center',
        marginTop: 14,
      }}
    >
      <Typography variant="h6" fontWeight={750}>{isEmpty ? 'No claims yet' : 'No claims match your filters'}</Typography>
      <Typography sx={{ color: '#64748B', mt: 1, mb: 2 }}>
        {isEmpty ? 'Create the first claim to begin tracking payer outcomes.' : 'Adjust the search, status, or payer filters.'}
      </Typography>
      {isEmpty ? (
        <Button variant="contained" startIcon={<AddIcon size={16} />} onClick={onCreate}>Create Claim</Button>
      ) : (
        <Button variant="outlined" onClick={onClear}>Clear filters</Button>
      )}
    </div>
  );
}

function OptumSyncDialog({
  open,
  syncing,
  syncError,
  syncStart,
  syncEnd,
  onClose,
  onConfirm,
  onStartChange,
  onEndChange,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Sync from Optum</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Start date"
            type="date"
            value={syncStart}
            onChange={(e) => onStartChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={syncing}
            fullWidth
          />
          <TextField
            label="End date"
            type="date"
            value={syncEnd}
            onChange={(e) => onEndChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={syncing}
            fullWidth
          />
          {syncError && <Alert severity="error">{syncError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={syncing}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={syncing || !syncStart || !syncEnd}
          startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <RefreshCw size={16} />}
        >
          {syncing ? 'Syncing...' : 'Sync'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ClaimListPage;
